"use client";

import { Alert, Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { useMemo, useRef } from "react";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import useVoiceRecorder from "../hooks/useVoiceRecorder";
import type { RecordedAudioPayload } from "../types";

interface AudioRecorderPanelProps {
  canTranscribeAndSubmit?: boolean;
  isTranscribing?: boolean;
  transcribeAndSubmitDisabledReason?: string;
  transcribeAndSubmitLabel?: string;
  transcribeAndSubmitLoadingText?: string;
  onReset?: () => void;
  onTranscribe: (recording: RecordedAudioPayload) => Promise<void> | void;
  onTranscribeAndSubmit?: (
    recording: RecordedAudioPayload,
  ) => Promise<void> | void;
}

function formatSeconds(value: number): string {
  const totalSeconds = Math.max(0, Math.floor(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function getRecordingActionLabel(status: ReturnType<typeof useVoiceRecorder>["status"]) {
  return status === "recording" ? "Stop recording" : "Start recording";
}

export default function AudioRecorderPanel({
  canTranscribeAndSubmit = true,
  isTranscribing = false,
  transcribeAndSubmitDisabledReason,
  transcribeAndSubmitLabel = "Transcribe & submit",
  transcribeAndSubmitLoadingText = "Submitting",
  onReset,
  onTranscribe,
  onTranscribeAndSubmit,
}: AudioRecorderPanelProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const {
    error,
    notice,
    elapsedMs,
    isSupported,
    maxDurationSeconds,
    recording,
    resetRecording,
    startRecording,
    status,
    stopRecording,
  } = useVoiceRecorder();

  const elapsedSeconds = elapsedMs / 1000;
  const canReplay = !!recording && status !== "recording" && !isTranscribing;
  const canReset = (!!recording || status === "recording") && !isTranscribing;
  const canTranscribe =
    !!recording && status !== "recording" && !isTranscribing;
  const canUseTranscribeAndSubmit =
    canTranscribe && canTranscribeAndSubmit && Boolean(onTranscribeAndSubmit);
  const recordingActionLabel = getRecordingActionLabel(status);
  const recordingIcon = status === "recording" ? "stop" : "microphone";

  const statusText = useMemo(() => {
    if (status === "recording") {
      return `Recording ${formatSeconds(elapsedSeconds)} / ${formatSeconds(
        maxDurationSeconds,
      )}`;
    }

    if (recording) {
      return `Recorded ${formatSeconds(recording.durationSeconds)} source audio.`;
    }

    return `Record source material up to ${formatSeconds(maxDurationSeconds)}.`;
  }, [elapsedSeconds, maxDurationSeconds, recording, status]);

  const handleReplay = async () => {
    if (!audioRef.current) {
      return;
    }

    audioRef.current.currentTime = 0;
    await audioRef.current.play();
  };

  const handleReset = () => {
    resetRecording();
    onReset?.();
  };

  if (!isSupported) {
    return (
      <Alert.Root status="error" borderRadius="lg">
        <Alert.Indicator />
        <Alert.Description>
          Voice recording is not supported on this device or browser.
        </Alert.Description>
      </Alert.Root>
    );
  }

  return (
    <Stack gap="4">
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={{ base: "3", md: "4" }}
        bg="moveon.surface.elevatedBg"
        width="full"
      >
        <Stack gap="3">
          <HStack gap="2" align="center">
            <GenericIcon icon="microphone" humanReadable="Voice recording" />
            <Text fontWeight="medium">Voice recording</Text>
          </HStack>
          <Text fontSize="sm" color="moveon.text.muted">
            {statusText}
          </Text>

          <Stack
            direction={{ base: "column", sm: "row" }}
            gap={{ base: "2", sm: "3" }}
            align={{ base: "stretch", sm: "center" }}
            wrap={{ base: "nowrap", sm: "wrap" }}
          >
            <Button
              aria-label={recordingActionLabel}
              title={recordingActionLabel}
              onClick={status === "recording" ? stopRecording : startRecording}
              colorPalette={status === "recording" ? "red" : "blue"}
              loading={isTranscribing}
              width={{ base: "full", sm: "auto" }}
            >
              <GenericIcon
                icon={recordingIcon}
                humanReadable={recordingActionLabel}
              />
              <Box as="span">{recordingActionLabel}</Box>
            </Button>

            <HStack gap="2" justify={{ base: "space-between", sm: "flex-start" }}>
              <Button
                aria-label="Replay recorded source audio"
                title="Replay recorded source audio"
                variant="outline"
                onClick={handleReplay}
                disabled={!canReplay}
                flex={{ base: "1", sm: "0 0 auto" }}
              >
                <GenericIcon icon="replay" humanReadable="Replay recording" />
                <Box as="span">Replay</Box>
              </Button>

              <Button
                aria-label="Clear recorded source audio"
                title="Clear recorded source audio"
                variant="outline"
                color="moveon.warning"
                borderColor="moveon.warningBorder"
                onClick={handleReset}
                disabled={!canReset}
                flex={{ base: "1", sm: "0 0 auto" }}
                _hover={{
                  bg: "moveon.warningLight",
                }}
              >
                <GenericIcon icon="reset" humanReadable="Clear recording" />
                <Box as="span">Clear</Box>
              </Button>
            </HStack>

            <Button
              aria-label="Transcribe recorded source audio"
              title="Transcribe recorded source audio"
              bg="moveon.primary"
              onClick={() => recording && onTranscribe(recording)}
              disabled={!canTranscribe}
              loading={isTranscribing}
              loadingText="Transcribing"
              width={{ base: "full", sm: "auto" }}
              color="white"
            >
              <GenericIcon icon="transcribe" humanReadable="Transcribe" />
              <Box as="span">Transcribe</Box>
            </Button>

            {onTranscribeAndSubmit ? (
              <Button
                aria-label={transcribeAndSubmitLabel}
                title={transcribeAndSubmitLabel}
                variant="outline"
                onClick={() => recording && onTranscribeAndSubmit(recording)}
                disabled={!canUseTranscribeAndSubmit}
                loading={isTranscribing}
                loadingText={transcribeAndSubmitLoadingText}
                width={{ base: "full", sm: "auto" }}
              >
                <GenericIcon
                  icon="transcribe"
                  humanReadable={transcribeAndSubmitLabel}
                />
                <Box as="span">{transcribeAndSubmitLabel}</Box>
              </Button>
            ) : null}
          </Stack>

          {onTranscribeAndSubmit && transcribeAndSubmitDisabledReason ? (
            <Text fontSize="xs" color="moveon.text.muted">
              {transcribeAndSubmitDisabledReason}
            </Text>
          ) : null}
        </Stack>
      </Box>

      {recording && (
        <audio
          ref={audioRef}
          controls
          preload="metadata"
          src={recording.objectUrl}
          style={{ width: "100%" }}
        />
      )}

      {notice && (
        <Alert.Root status="info" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Description>{notice}</Alert.Description>
        </Alert.Root>
      )}

      {error && (
        <Alert.Root status="error" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Description>{error}</Alert.Description>
        </Alert.Root>
      )}
    </Stack>
  );
}
